import type { ApolloError } from '@apollo/client';
import { order_by } from '../lib/generated/zeus';
import { useTypedQuery } from '../lib/generated/zeus/apollo';
import { genericScalars } from '../lib/graphql/helpers';
import { costObjectSelector } from './selectors/team';
import type { StoredCostObject } from './types';
import { objValueNullToUndefined } from '../lib/utils';

export const costObjectIdsQueryName = 'queryCostObjectIds';

/**
 * Provides GraphQL query to fetch all cost object ids.
 * Note that the query is cached under query name {@link costObjectIdsQueryName}, so less network round trip unless refetch.
 * @param onError callback to handle errors.
 * @returns custom hook that provides GraphQL query to query all Teams.
 */
const useQueryCostObjectIds = (
  search: string,
  onCompleted?: (d: readonly StoredCostObject[]) => void,
  onError?: (e: ApolloError) => void
) => {
  const result = useTypedQuery(
    {
      cost_object: [
        {
          where: {
            _or: [
              {
                internal_order_number: { _ilike: `%${search}%` },
              },
              {
                description: { _ilike: `%${search}%` },
              },
            ],
          },
          order_by: [{ internal_order_number: order_by.asc }],
        },
        costObjectSelector,
      ],
    },
    {
      scalars: genericScalars,
      apolloOptions: {
        onCompleted: (data) => {
          if (onCompleted !== undefined) {
            const queriedCostObjects: readonly StoredCostObject[] =
              data.cost_object
                .map((cost_object) =>
                  objValueNullToUndefined<StoredCostObject>(cost_object)
                )
                .filter(
                  (cost_object): cost_object is StoredCostObject =>
                    cost_object !== undefined
                );

            onCompleted(queriedCostObjects);
          }
        },
        onError,
      },
      operationOptions: {
        operationName: costObjectIdsQueryName,
      },
    }
  );

  return result;
};

export default useQueryCostObjectIds;
